import React from 'react';
import { Check, AlertTriangle, X, Star } from 'lucide-react';
import { Checkbox } from '../../ui/checkbox';
import { formatCurrency, formatPercent } from '../formatters';

const StatusIcon = ({ status }) => {
  const icons = {
    viable: <Check className="w-5 h-5" />,
    risky: <AlertTriangle className="w-5 h-5" />,
    'non-viable': <X className="w-5 h-5" />,
  };
  return icons[status] || icons['non-viable'];
};

export const HypothesisCard = ({ hypothesis, isRecommended, number, t, isIncluded, onToggleInclude, recommendedCommissionRate, disabled }) => {
  // Modern status config - subtle top border only
  const statusConfig = {
    viable: {
      topBorder: 'border-t-emerald-500',
      icon: 'text-emerald-600',
      badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200'
    },
    risky: {
      topBorder: 'border-t-amber-500',
      icon: 'text-amber-600',
      badge: 'bg-amber-50 text-amber-700 border border-amber-200'
    },
    'non-viable': {
      topBorder: 'border-t-rose-500',
      icon: 'text-rose-600',
      badge: 'bg-rose-50 text-rose-700 border border-rose-200'
    },
  };

  const config = statusConfig[hypothesis.status] || statusConfig['non-viable'];
  const commissionRate = recommendedCommissionRate || hypothesis.commission_rate;
  const totalCommission = hypothesis.ca_ht_annual * commissionRate;

  return (
    <div
      className={`
        relative bg-white rounded-xl border border-zinc-200 
        border-t-4 ${config.topBorder}
        shadow-sm hover:shadow-md hover:border-zinc-300
        transition-all duration-200 
        ${!isIncluded ? 'opacity-40 scale-[0.98]' : ''} 
        ${disabled ? 'pointer-events-none' : ''}
        ${isIncluded ? 'ring-1 ring-zinc-900/5' : ''}
      `}
      data-testid={`hypothesis-card-${number}`}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute -top-2 right-4 bg-zinc-900 text-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wider rounded-full flex items-center gap-1.5 shadow-lg">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          {t('recommended')}
        </div>
      )}

      {/* Header */}
      <div className="p-5 pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={isIncluded}
              onCheckedChange={onToggleInclude}
              className="mt-0.5 h-5 w-5 rounded-md border-2 border-zinc-300 data-[state=checked]:bg-zinc-900 data-[state=checked]:border-zinc-900"
              disabled={disabled}
              data-testid={`checkbox-hypothesis-${number}`}
            />
            <div>
              <h3 className="text-base font-semibold text-zinc-900 tracking-tight">
                {t(`hypothesis${number}`)}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                {number === 1 && 'Basée sur la fréquentation'}
                {number === 2 && 'Basée sur le CA alimentaire'}
                {number === 3 && 'Basée sur la poissonnerie'}
              </p>
            </div>
          </div>
          <div className={`p-2 rounded-lg bg-zinc-50 ${config.icon}`}>
            <StatusIcon status={hypothesis.status} />
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="px-5 pb-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-zinc-50 rounded-lg p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{t('caTTCAnnual')}</p>
            <p className="text-lg font-mono font-bold text-zinc-900 mt-1">
              {formatCurrency(hypothesis.ca_ttc_annual)}
            </p>
          </div>
          <div className="bg-zinc-50 rounded-lg p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{t('caHTAnnual')}</p>
            <p className="text-lg font-mono font-bold text-zinc-900 mt-1">
              {formatCurrency(hypothesis.ca_ht_annual)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{t('totalCommission')}</p>
            <p className="text-sm font-mono font-semibold text-zinc-700 mt-0.5">
              {formatCurrency(totalCommission)}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{t('commissionRate')}</p>
            <p className="text-sm font-mono font-semibold text-zinc-700 mt-0.5">
              {formatPercent(commissionRate)}
            </p>
          </div>
        </div>
      </div>

      {/* Footer - Net Margin */}
      <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50/50 rounded-b-xl">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{t('netMargin')}</p>
            <p className={`text-xl font-mono font-bold mt-0.5 ${
              hypothesis.net_margin_year_n >= 0
                ? 'text-emerald-600'
                : 'text-rose-600'
            }`}>
              {formatCurrency(hypothesis.net_margin_year_n)}
            </p>
          </div>
          <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${config.badge}`}>
            {hypothesis.status === 'viable' && t('viable')}
            {hypothesis.status === 'risky' && t('risky')}
            {hypothesis.status === 'non-viable' && t('nonViable')}
          </span>
        </div>
      </div>
    </div>
  );
};
